/**
 * Validación del espacio (prompt §7; mínima en la rebanada 1.0): recursos mínimos para simular,
 * conexión ingreso → estaciones → salida, parámetros coherentes y avisos del layout.
 * Nunca lanza errores: devuelve observaciones accionables.
 */

import type {
  Catalogo,
  Elemento,
  Espacio,
  ItemDeCatalogo,
  Punto,
  TipoVia,
} from '../dominio/espacio';
import type { Observacion } from '../dominio/observacion';
import { ROLES, type PuntoDePersonas, type Rol, type TipoEstacion } from '../dominio/proceso';
import { distanciasAPie, puntosClave } from '../espacial/aPie';
import { esHoraValida, horaASegundos } from '../dominio/tiempo';
import { tramosVehiculares } from '../espacial/circulacion';
import { compilarEspacio, NOMBRE_ESTACION, type ModeloEspacial } from '../espacial/compilador';
import { huellasDeMuro } from '../espacial/formas';
import {
  encoger,
  huella,
  poligonosSuperpuestos,
  segmentoTocaPoligono,
} from '../espacial/geometria';
import { estimarCapacidad, observacionesDeCapacidad, type EstimacionCapacidad } from './capacidad';
import { lugaresEfectivos, siguientesPosibles, validarProceso } from './proceso';

export interface ResultadoValidacion {
  observaciones: Observacion[];
  modelo: ModeloEspacial;
  bloquea: boolean;
  /** Capacidad estimada; `null` si el proceso o los parámetros aún no son válidos. */
  capacidad: EstimacionCapacidad | null;
}

const NOMBRE_ROL: Record<Rol, string> = {
  asesor: 'asesor',
  tecnico: 'técnico',
  calidad: 'responsable de control de calidad',
  controlador: 'controlador de torre',
};
const PLURAL_ROL: Record<Rol, string> = {
  asesor: 'asesores',
  tecnico: 'técnicos',
  calidad: 'responsables de control de calidad',
  controlador: 'controladores de torre',
};

const NOMBRE_PUNTO: Record<PuntoDePersonas, string> = {
  oficina: 'la oficina de asesores',
  almacen: 'el almacén de repuestos',
  torre: 'la torre de control',
  'acceso-peatonal': 'el acceso peatonal',
};

const NOMBRE_VIA: Record<TipoVia, string> = {
  'vehicular-doble': 'Vía de doble sentido',
  'vehicular-un-sentido': 'Vía de un sentido',
  peatonal: 'Senda peatonal',
};

/** Nombre de un elemento en un mensaje: con su código si es una estación (Elevador B03). */
function nombreDe(e: Elemento, item: ItemDeCatalogo, modelo: ModeloEspacial): string {
  const s = modelo.estaciones.find((x) => x.elementoId === e.id);
  if (!s || s.tipo === 'ingreso' || s.tipo === 'salida') return item.nombre;
  return `${item.nombre} ${s.id}`;
}

const ARTICULO: Record<TipoEstacion, string> = {
  bahia: 'un elevador activo',
  recepcion: 'un cajón de recepción',
  patio: 'un cajón de patio',
  control: 'una bahía de espera de control',
  lavado: 'un puesto de lavado',
  entrega: 'un cajón de entrega',
  ingreso: 'un ingreso vehicular',
  salida: 'una salida vehicular',
};

export function validarEspacio(espacio: Espacio, catalogo: Catalogo): ResultadoValidacion {
  const modelo = compilarEspacio(espacio, catalogo);
  const r: Observacion[] = [...modelo.observaciones];
  const proceso = espacio.modelo.proceso;

  // Contenido fuera del espacio: no se simula (doc. 03 §4, regla 3).
  for (const id of modelo.fueraDelEspacio) {
    const e = espacio.layout.elementos.find((x) => x.id === id);
    const nombre = e ? (catalogo[e.catalogoId]?.nombre ?? 'Elemento') : 'Elemento';
    r.push({
      regla: 'fuera-del-espacio',
      nivel: 'advierte',
      que: `${nombre} fuera del espacio`,
      donde: `${nombre} en x = ${e?.posicion.x.toFixed(1)} m, z = ${e?.posicion.z.toFixed(1)} m`,
      porque:
        'Lo que queda fuera del marco del espacio no forma parte de la operación y no se simula.',
      queHacer: 'Muévelo dentro del marco o elimínalo.',
      elementos: [id],
    });
  }

  // Elementos con el centro dentro pero una parte fuera del marco: en el terreno real no caben. Los
  // marcadores (ingreso y salida) pueden quedar sobre el borde, como un portón.
  const fuera = new Set(modelo.fueraDelEspacio);
  const { marco } = espacio;
  for (const e of espacio.layout.elementos) {
    const item = catalogo[e.catalogoId];
    if (!item || fuera.has(e.id) || item.boca?.forma === 'marcador') continue;
    const sobresale = huella(e).some(
      (p) =>
        Math.abs(p.x - marco.x) > marco.ancho / 2 + 0.01 ||
        Math.abs(p.z - marco.z) > marco.fondo / 2 + 0.01,
    );
    if (!sobresale) continue;
    r.push({
      regla: 'sobresale-del-espacio',
      nivel: 'advierte',
      que: `${item.nombre} sobresale del espacio`,
      donde: `${item.nombre} en x = ${e.posicion.x.toFixed(1)} m, z = ${e.posicion.z.toFixed(1)} m`,
      porque: 'Una parte del elemento queda fuera del marco: en el terreno real no cabría.',
      queHacer: 'Muévelo hacia dentro del marco o agranda el espacio.',
      elementos: [e.id],
    });
  }

  // Elementos únicos repetidos.
  const conteo = new Map<string, string[]>();
  for (const e of espacio.layout.elementos) {
    if (!catalogo[e.catalogoId]?.unico) continue;
    conteo.set(e.catalogoId, [...(conteo.get(e.catalogoId) ?? []), e.id]);
  }
  for (const [catalogoId, ids] of conteo) {
    if (ids.length < 2) continue;
    const { nombre, simula } = catalogo[catalogoId]!;
    r.push({
      regla: 'elemento-unico-repetido',
      nivel: 'advierte',
      que: `Hay ${ids.length} elementos «${nombre}»`,
      donde: nombre,
      porque: simula
        ? 'El simulador usa solo el primero; los demás no cambian los resultados.'
        : 'Un taller tiene uno solo; cuando este elemento se simule, se usará el primero.',
      queHacer: 'Elimina los que sobran.',
      elementos: ids.slice(1),
    });
  }

  // Medidas mínimas.
  for (const e of espacio.layout.elementos) {
    const item = catalogo[e.catalogoId];
    const m = item?.medidasMinimas;
    if (
      item &&
      m &&
      (e.dimensiones.ancho < m.ancho - 1e-6 || e.dimensiones.fondo < m.fondo - 1e-6)
    ) {
      r.push({
        regla: 'medida-minima',
        nivel: 'advierte',
        que: `${item.nombre} más pequeño que un auto`,
        donde: `${item.nombre} de ${e.dimensiones.ancho.toFixed(1)} × ${e.dimensiones.fondo.toFixed(1)} m`,
        porque: `El mínimo para un auto es ${m.ancho.toFixed(1)} × ${m.fondo.toFixed(1)} m.`,
        queHacer: 'Agranda el elemento.',
        elementos: [e.id],
      });
    }
  }
  // Superposiciones entre elementos sólidos. Como en el v6, los equipos y el mobiliario pueden
  // estar dentro de un contenedor (oficina, almacén, edificio, sala de espera) sin aviso.
  const huellas = espacio.layout.elementos
    .filter((e) => catalogo[e.catalogoId]?.solido)
    .map((e) => ({ e, item: catalogo[e.catalogoId]!, h: huella(e) }));
  const mueble = (i: ItemDeCatalogo) => i.categoria === 'equipos' || i.categoria === 'clientes';
  for (let i = 0; i < huellas.length; i++) {
    for (let j = i + 1; j < huellas.length; j++) {
      const a = huellas[i]!;
      const b = huellas[j]!;
      if ((a.item.contenedor && mueble(b.item)) || (b.item.contenedor && mueble(a.item))) continue;
      if (!poligonosSuperpuestos(a.h, b.h)) continue;
      const na = catalogo[a.e.catalogoId]?.nombre ?? 'Elemento';
      const nb = catalogo[b.e.catalogoId]?.nombre ?? 'Elemento';
      r.push({
        regla: 'superposicion',
        nivel: 'advierte',
        que: `Superposición: ${na} con ${nb}`,
        donde: `${na} y ${nb}`,
        porque:
          'Dos elementos físicos no pueden ocupar el mismo lugar; el plano no sería realista.',
        queHacer: 'Separa los elementos.',
        elementos: [a.e.id, b.e.id],
      });
    }
  }

  // Elementos sólidos que pisan el eje de una vía vehicular (como en el v6): la cierran.
  const tramos = tramosVehiculares(espacio.layout.vias);
  for (const { e, item, h } of huellas) {
    const poligono = encoger(h, 0.25);
    if (!tramos.some((t) => segmentoTocaPoligono(t.a, t.b, poligono))) continue;
    const nombre = nombreDe(e, item, modelo);
    r.push({
      regla: 'via-bloqueada',
      nivel: 'advierte',
      que: `${nombre} bloquea una vía`,
      donde: `${nombre} en x = ${e.posicion.x.toFixed(1)} m, z = ${e.posicion.z.toFixed(1)} m`,
      porque:
        'El elemento ocupa la calzada: en el taller real los autos no podrían pasar por ahí, aunque la ruta calculada lo haga.',
      queHacer: 'Muévelo fuera de la calzada o mueve la vía.',
      elementos: [e.id],
    });
  }

  // Pisos y muros (paso 1.4): lo que queda fuera del marco o sobresale, y muros sobre el eje de una
  // vía vehicular.
  const afuera = (q: Punto) =>
    Math.abs(q.x - marco.x) > marco.ancho / 2 + 0.01 ||
    Math.abs(q.z - marco.z) > marco.fondo / 2 + 0.01;
  const formas = [
    ...espacio.layout.pisos.map((f) => ({ f, nombre: 'Piso' })),
    ...espacio.layout.muros.map((f) => ({ f, nombre: 'Muro' })),
  ];
  for (const { f, nombre } of formas) {
    const n = f.puntos.filter(afuera).length;
    if (n === 0) continue;
    const xs = f.puntos.map((q) => q.x);
    const zs = f.puntos.map((q) => q.z);
    const todoFuera =
      Math.min(...xs) >= marco.x + marco.ancho / 2 ||
      Math.max(...xs) <= marco.x - marco.ancho / 2 ||
      Math.min(...zs) >= marco.z + marco.fondo / 2 ||
      Math.max(...zs) <= marco.z - marco.fondo / 2;
    r.push({
      regla: todoFuera ? 'fuera-del-espacio' : 'sobresale-del-espacio',
      nivel: 'advierte',
      que: `${nombre} ${todoFuera ? 'fuera del' : 'sobresale del'} espacio`,
      donde: `${nombre} con ${n} de ${f.puntos.length} puntos fuera del marco`,
      porque: todoFuera
        ? 'Lo que queda fuera del marco del espacio no forma parte de la operación.'
        : 'Una parte queda fuera del marco: en el terreno real no cabría.',
      queHacer: 'Mueve sus puntos hacia dentro del marco, agranda el espacio o elimínalo.',
      elementos: [f.id],
    });
  }
  for (const m of espacio.layout.muros) {
    const huellasMuro = huellasDeMuro(m).map((h) => encoger(h, 0.02));
    if (!tramos.some((t) => huellasMuro.some((h) => segmentoTocaPoligono(t.a, t.b, h)))) continue;
    r.push({
      regla: 'via-bloqueada',
      nivel: 'advierte',
      que: 'Un muro bloquea una vía',
      donde: `Muro desde x = ${m.puntos[0]!.x.toFixed(1)} m, z = ${m.puntos[0]!.z.toFixed(1)} m`,
      porque:
        'El muro cruza la calzada: en el taller real los autos no podrían pasar, aunque la ruta calculada lo haga.',
      queHacer: 'Mueve el muro o deja un vano donde pasa la vía.',
      elementos: [m.id],
    });
  }

  // Vías con un extremo suelto (paso 1.4).
  for (const s of modelo.extremosSueltos) {
    const via = espacio.layout.vias.find((v) => v.id === s.viaId);
    const nombre = via ? NOMBRE_VIA[via.tipo] : 'Vía';
    r.push({
      regla: 'via-suelta',
      nivel: 'advierte',
      que: s.sobreOtraVia
        ? `${nombre} suelta: un extremo pisa otra vía sin unirse a ella`
        : `${nombre} suelta: un extremo no se une a nada`,
      donde: `${nombre}, extremo en x = ${s.punto.x.toFixed(1)} m, z = ${s.punto.z.toFixed(1)} m`,
      porque: s.sobreOtraVia
        ? 'Las calzadas se tocan, pero el empalme no está hecho: la ruta no pasa de una vía a la otra.'
        : 'Los autos no pueden seguir más allá de ese extremo. Si querías unirla a otra vía, el empalme no quedó hecho.',
      queHacer:
        'Selecciona la vía y arrastra ese extremo hacia la otra vía: al acercarlo se une sola (imán). Si es un fondo de saco a propósito, puedes ignorar este aviso.',
      elementos: [s.viaId],
    });
  }

  // Proceso y parámetros.
  r.push(...validarProceso(proceso));
  r.push(...validarParametros(espacio));

  // Recursos mínimos: cada lugar que el proceso usa debe existir en el layout.
  const porTipo = new Map<TipoEstacion, typeof modelo.estaciones>();
  for (const s of modelo.estaciones) porTipo.set(s.tipo, [...(porTipo.get(s.tipo) ?? []), s]);
  const requeridos = new Set<TipoEstacion>(['ingreso', 'salida']);
  for (const e of proceso.etapas) {
    if (e.tipo === 'salida') requeridos.add('salida');
    else if (e.lugar !== 'actual') requeridos.add(e.lugar);
  }
  for (const tipo of requeridos) {
    if ((porTipo.get(tipo) ?? []).length > 0) continue;
    r.push({
      regla: 'recurso-minimo',
      nivel: 'bloquea',
      que: `Falta ${ARTICULO[tipo]}`,
      donde: 'Espacio completo',
      porque: `El proceso necesita ${ARTICULO[tipo]} y sin él los autos no pueden completar su recorrido.`,
      queHacer: `Agrega ${ARTICULO[tipo]} desde el catálogo${tipo === 'bahia' ? ' (o activa uno existente)' : ''}.`,
      elementos: [],
    });
  }
  // Elevadores compatibles con cada tipo de unidad que los necesita.
  for (const tipo of proceso.tiposDeUnidad) {
    const usaBahia = proceso.etapas.some(
      (e) => e.lugar === 'bahia' && alcanzaEtapa(espacio, e.id, tipo.id),
    );
    if (!usaBahia || (porTipo.get('bahia') ?? []).length === 0) continue;
    const compatibles = (porTipo.get('bahia') ?? []).filter(
      (b) => b.compatibles === null || b.compatibles.includes(tipo.id),
    );
    if (compatibles.length === 0) {
      r.push({
        regla: 'bahia-compatible',
        nivel: 'bloquea',
        que: `Ningún elevador atiende «${tipo.nombre}»`,
        donde: 'Elevadores del espacio',
        porque: `Los autos de «${tipo.nombre}» no tendrían dónde atenderse.`,
        queHacer: `Cambia el tipo de algún elevador, o marca en «Servicios» otros tipos de elevador para «${tipo.nombre}».`,
        elementos: [],
      });
    }
  }
  // Personas mínimas.
  const roles = new Set<Rol>(proceso.etapas.flatMap((e) => e.personas.map((p) => p.rol)));
  for (const rol of ROLES) {
    if (!roles.has(rol)) continue;
    const n = espacio.modelo.dotacion[rol]?.valor ?? 0;
    if (n < 1) {
      r.push({
        regla: 'persona-minima',
        nivel: 'bloquea',
        que: `No hay ningún ${NOMBRE_ROL[rol]}`,
        donde: 'Parámetros · Personas',
        porque: `El proceso necesita al menos un ${NOMBRE_ROL[rol]}.`,
        queHacer: `Ingresa al menos 1 ${NOMBRE_ROL[rol]}.`,
        elementos: [],
      });
    }
  }

  // Técnico fijo por elevador (F3, ADR 0021): con distinto número, lo que sobra no trabaja.
  if (espacio.modelo.tecnicoPorElevador?.valor === 'fijo') {
    const bahias = modelo.estaciones
      .filter((s) => s.tipo === 'bahia')
      .map((s) => s.id)
      .sort();
    const tecnicos = espacio.modelo.dotacion.tecnico.valor;
    const lista = (xs: string[]) =>
      xs.length === 1 ? xs[0]! : `${xs.slice(0, -1).join(', ')} y ${xs[xs.length - 1]!}`;
    if (bahias.length > tecnicos && tecnicos > 0) {
      const sinTecnico = bahias.slice(tecnicos);
      r.push({
        regla: 'tecnico-fijo-sin-tecnico',
        nivel: 'advierte',
        que: `${sinTecnico.length === 1 ? 'El elevador' : 'Los elevadores'} ${lista(sinTecnico)} no ${sinTecnico.length === 1 ? 'tiene' : 'tienen'} técnico fijo`,
        donde: 'Parámetros · Políticas · Cada técnico trabaja en',
        porque: `Con «un elevador fijo» hay ${bahias.length} elevadores y ${tecnicos} técnicos: cada técnico trabaja en el suyo (T01 en el primero, por código) y un elevador sin técnico no se usa para trabajos con técnico.`,
        queHacer:
          'Agrega técnicos, deja esos elevadores como inactivos o elige «el elevador que esté libre».',
        elementos: [],
      });
    }
    if (tecnicos > bahias.length && bahias.length > 0) {
      const sinElevador = Array.from(
        { length: tecnicos - bahias.length },
        (_, i) => `T${String(bahias.length + i + 1).padStart(2, '0')}`,
      );
      r.push({
        regla: 'tecnico-fijo-sin-elevador',
        nivel: 'advierte',
        que: `${sinElevador.length === 1 ? 'El técnico' : 'Los técnicos'} ${lista(sinElevador)} no ${sinElevador.length === 1 ? 'tiene' : 'tienen'} elevador fijo`,
        donde: 'Parámetros · Políticas · Cada técnico trabaja en',
        porque: `Con «un elevador fijo» hay ${tecnicos} técnicos y ${bahias.length} elevadores: un técnico sin elevador no trabaja en elevadores.`,
        queHacer: 'Agrega elevadores, reduce los técnicos o elige «el elevador que esté libre».',
        elementos: [],
      });
    }
  }

  // Recorridos a pie (paso 1.6, avance 2): los puntos que usan deben existir; sin red de vías,
  // las personas caminan en línea recta (doc. 04 §9), lo que se advierte si el movimiento es por
  // distancia.
  const recorridos = proceso.etapas.flatMap((e) =>
    (e.recorridos ?? []).map((rc) => ({ etapa: e, rc })),
  );
  if (recorridos.length > 0) {
    const presentes = puntosClave(espacio);
    const faltan = new Map<PuntoDePersonas, string>();
    for (const { etapa, rc } of recorridos) {
      for (const p of [rc.desde, rc.hacia]) {
        if (p === 'auto' || p === 'donde-esta' || presentes.has(p)) continue;
        if (!faltan.has(p)) faltan.set(p, etapa.nombre);
      }
    }
    for (const [punto, etapa] of faltan) {
      r.push({
        regla: 'recorrido-sin-punto',
        nivel: 'bloquea',
        que: `Falta ${NOMBRE_PUNTO[punto]} en el espacio`,
        donde: `Etapa «${etapa}»`,
        porque: `La etapa tiene un recorrido a pie que va o viene de ${NOMBRE_PUNTO[punto]}, y sin ese punto no se puede calcular cuánto caminan las personas.`,
        queHacer: `Coloca ${NOMBRE_PUNTO[punto]} desde el catálogo («Puntos clave») dentro del espacio.`,
        elementos: [],
      });
    }
    // Con el traslado fijo (parámetro de prueba) los tramos a pie no usan la distancia.
    if (
      faltan.size === 0 &&
      espacio.modelo.movimiento.modo === 'distancia' &&
      distanciasAPie(espacio, modelo.estaciones).enLineaRecta
    ) {
      r.push({
        regla: 'recorrido-en-linea-recta',
        nivel: 'advierte',
        que: 'Las personas caminan en línea recta',
        donde: 'Vías y sendas del espacio',
        porque:
          'Algún recorrido a pie no encuentra un camino por las vías: se calcula en línea recta, lo que puede acortar el tiempo a pie.',
        queHacer: 'Dibuja sendas peatonales o vías que unan esos puntos.',
        elementos: [],
      });
    }
  }

  // Conexión: para cada paso del proceso, cada estación de origen alcanza alguna de destino.
  if (!r.some((o) => o.regla === 'recurso-minimo' || o.regla.startsWith('proceso-'))) {
    const indice = new Map(modelo.estaciones.map((s, i) => [s, i]));
    const lugares = lugaresEfectivos(proceso);
    const pares = new Set<string>();
    const inicial = lugares.get(proceso.etapaInicial) ?? new Set();
    for (const l of inicial) if (l !== 'ingreso') pares.add(`ingreso>${l}`);
    const hayPatio = (porTipo.get('patio') ?? []).length > 0;
    for (const t of proceso.transiciones) {
      const destino = proceso.etapas.find((e) => e.id === t.a);
      for (const a of lugares.get(t.de) ?? []) {
        for (const b of lugares.get(t.a) ?? []) {
          if (a !== b) pares.add(`${a}>${b}`);
          if (destino?.lugarDeEspera === 'patio' && hayPatio) {
            if (a !== 'patio') pares.add(`${a}>patio`);
            if (b !== 'patio') pares.add(`patio>${b}`);
          }
        }
      }
    }
    for (const par of pares) {
      const [a, b] = par.split('>') as [TipoEstacion, TipoEstacion];
      const origenes = porTipo.get(a) ?? [];
      const destinos = porTipo.get(b) ?? [];
      if (destinos.length === 0) continue;
      for (const o of origenes) {
        const i = indice.get(o)!;
        const alcanza = destinos.some((d) => modelo.distancias[i]![indice.get(d)!] !== null);
        if (alcanza || !o.bocaSalida) continue;
        const nombreO =
          o.tipo === 'ingreso' || o.tipo === 'salida'
            ? NOMBRE_ESTACION[o.tipo]
            : `${NOMBRE_ESTACION[o.tipo]} ${o.id}`;
        r.push({
          regla: 'sin-ruta',
          nivel: 'bloquea',
          que: `${nombreO} no tiene ruta hacia ${ARTICULO[b]}`,
          donde: nombreO,
          porque: `Los autos deben ir de ${NOMBRE_ESTACION[a].toLowerCase()} a ${NOMBRE_ESTACION[b].toLowerCase()} y las vías no lo permiten.`,
          queHacer:
            'Revisa que las vías estén conectadas y que los sentidos únicos permitan el recorrido.',
          elementos: [o.elementoId],
        });
      }
    }
  }

  // Capacidad estimada (advierte, no bloquea): solo con proceso y parámetros válidos.
  let capacidad: EstimacionCapacidad | null = null;
  const datosValidos = !r.some(
    (o) =>
      o.nivel === 'bloquea' && (o.regla.startsWith('proceso-') || o.regla.startsWith('parametro-')),
  );
  if (datosValidos) {
    capacidad = estimarCapacidad(espacio, modelo.estaciones);
    r.push(...observacionesDeCapacidad(espacio, capacidad));
  }

  return { observaciones: r, modelo, bloquea: r.some((o) => o.nivel === 'bloquea'), capacidad };
}

function alcanzaEtapa(espacio: Espacio, etapaId: string, tipo: string): boolean {
  const proceso = espacio.modelo.proceso;
  const vistos = new Set<string>();
  const pila = [proceso.etapaInicial];
  while (pila.length) {
    const id = pila.pop()!;
    if (id === etapaId) return true;
    if (vistos.has(id)) continue;
    vistos.add(id);
    pila.push(...siguientesPosibles(proceso, id, tipo));
  }
  return false;
}

export function validarParametros(espacio: Espacio): Observacion[] {
  const r: Observacion[] = [];
  const m = espacio.modelo;
  const bloquea = (regla: string, que: string, donde: string, porque: string, queHacer: string) =>
    r.push({ regla, nivel: 'bloquea', que, donde, porque, queHacer, elementos: [] });

  const demanda = m.demanda.unidadesPorDia.valor;
  if (!Number.isInteger(demanda) || demanda < 0 || demanda > 500) {
    bloquea(
      'parametro-demanda',
      'Demanda inválida',
      'Parámetros · Demanda',
      'La demanda es la cantidad de autos citados por día.',
      'Ingresa un número entero entre 0 y 500.',
    );
  }
  const mezcla = m.demanda.mezcla.valor;
  const suma = Object.values(mezcla).reduce((a, b) => a + b, 0);
  if (Object.values(mezcla).some((v) => !(v >= 0 && v <= 1)) || Math.abs(suma - 1) > 0.001) {
    bloquea(
      'parametro-mezcla',
      'La mezcla de servicios no suma 100 %',
      'Parámetros · Demanda',
      'Cada auto debe ser de algún tipo de servicio.',
      'Ajusta los porcentajes para que sumen 100 %.',
    );
  }
  const perfil = m.demanda.perfilHorario.valor;
  if (
    m.demanda.modo === 'perfil' &&
    (perfil.some((v) => !(v >= 0)) || perfil.reduce((a, b) => a + b, 0) <= 0)
  ) {
    bloquea(
      'parametro-perfil',
      'Perfil de citas inválido',
      'Parámetros · Demanda',
      'Las citas se reparten por hora según el perfil.',
      'Usa fracciones positivas que sumen más de cero.',
    );
  }
  const horas = [
    m.jornada.inicioTurno,
    m.jornada.finTurno,
    m.jornada.inicioCitas,
    m.jornada.ultimaCita,
    m.jornada.cierre,
    m.atencion.inicio,
    m.atencion.fin,
  ];
  if (horas.some((h) => !esHoraValida(h.valor))) {
    bloquea(
      'parametro-hora',
      'Hay una hora con formato inválido',
      'Parámetros · Jornada',
      'Las horas se escriben como hh:mm.',
      'Corrige el formato (por ejemplo, 08:00).',
    );
  } else {
    const s = (h: { valor: string }) => horaASegundos(h.valor);
    if (!(
      s(m.jornada.inicioTurno) < s(m.jornada.finTurno) &&
      s(m.jornada.finTurno) <= s(m.jornada.cierre)
    )) {
      bloquea(
        'parametro-jornada',
        'La jornada no es coherente',
        'Parámetros · Jornada',
        'El turno debe empezar antes de terminar, y terminar antes o al cierre.',
        'Revisa inicio de turno, fin de turno y cierre.',
      );
    }
    if (s(m.jornada.inicioCitas) > s(m.jornada.ultimaCita)) {
      bloquea(
        'parametro-citas',
        'La primera cita es posterior a la última',
        'Parámetros · Jornada',
        'Las citas se reparten entre ambas horas.',
        'Corrige las horas de citas.',
      );
    }
    if (!(s(m.atencion.inicio) < s(m.atencion.fin))) {
      bloquea(
        'parametro-atencion',
        'Horario de atención incoherente',
        'Parámetros · Promesas',
        'La promesa se cuenta en horas hábiles de atención.',
        'El inicio debe ser anterior al fin.',
      );
    }
  }
  // Refrigerio de los técnicos (paso 1.2).
  const ref = m.refrigerio;
  if (ref) {
    const dur = ref.duracionMin.valor;
    if (!esHoraValida(ref.inicio.valor) || !(dur > 0 && dur <= 240)) {
      bloquea(
        'parametro-refrigerio',
        'Refrigerio inválido',
        'Parámetros · Jornada',
        'El refrigerio necesita una hora de inicio (hh:mm) y una duración entre 1 y 240 min.',
        'Corrige la hora o la duración, o quita el refrigerio.',
      );
    } else if (
      esHoraValida(m.jornada.inicioTurno.valor) &&
      esHoraValida(m.jornada.finTurno.valor)
    ) {
      const ini = horaASegundos(ref.inicio.valor);
      const fin = ini + dur * 60 * (ref.modo.valor === 'escalonado' ? 2 : 1);
      if (
        ini < horaASegundos(m.jornada.inicioTurno.valor) ||
        fin > horaASegundos(m.jornada.finTurno.valor)
      ) {
        r.push({
          regla: 'parametro-refrigerio-fuera-de-turno',
          nivel: 'advierte',
          que: 'El refrigerio queda fuera de la jornada de los técnicos',
          donde: 'Parámetros · Jornada',
          porque:
            'Un refrigerio que empieza antes del turno o termina después no descuenta tiempo dentro de la jornada.',
          queHacer: 'Revisa la hora de inicio, la duración o si es escalonado.',
          elementos: [],
        });
      }
    }
  }
  for (const rol of ROLES) {
    // Jefe de taller y controlador llegaron en la 1.6: un proyecto anterior no los tiene (cero).
    const opcional = rol !== 'asesor' && rol !== 'tecnico';
    const n = m.dotacion[rol]?.valor ?? (opcional ? 0 : undefined);
    if (n === undefined || !Number.isInteger(n) || n < 0 || n > 200) {
      bloquea(
        'parametro-dotacion',
        `Cantidad de ${PLURAL_ROL[rol]} inválida`,
        'Parámetros · Personas',
        'Las personas son recursos enteros.',
        'Ingresa un número entero entre 0 y 200.',
      );
    }
  }
  if (m.movimiento.modo === 'distancia' && !(m.movimiento.velocidadVehiculoKmh.valor > 0)) {
    bloquea(
      'parametro-velocidad',
      'Velocidad de traslado inválida',
      'Parámetros · Movimiento',
      'El tiempo de traslado es distancia ÷ velocidad.',
      'Ingresa una velocidad mayor que cero.',
    );
  }
  if (!(m.movimiento.maniobraS.valor >= 0)) {
    bloquea(
      'parametro-maniobra',
      'Tiempo de maniobra inválido',
      'Parámetros · Movimiento',
      'Se suma a cada traslado.',
      'Ingresa un valor mayor o igual a cero.',
    );
  }
  if (m.movimiento.modo === 'fijo' && !(m.movimiento.trasladoFijoMin.valor >= 0)) {
    bloquea(
      'parametro-traslado-fijo',
      'Traslado fijo inválido',
      'Parámetros · Movimiento',
      'Se usa como tiempo de cada traslado.',
      'Ingresa un valor mayor o igual a cero.',
    );
  }
  if (!(m.redondeoPromesaMin.valor >= 0)) {
    bloquea(
      'parametro-redondeo',
      'Redondeo de promesa inválido',
      'Parámetros · Promesas',
      'La hora prometida se redondea hacia arriba.',
      'Ingresa 0 (sin redondeo) o un número de minutos.',
    );
  }
  const ca = espacio.configuracionAleatoria;
  if (!Number.isInteger(ca.replicas) || ca.replicas < 2 || ca.replicas > 200) {
    bloquea(
      'parametro-replicas',
      'Número de réplicas inválido',
      'Simulación',
      'Se necesitan al menos 2 réplicas para estimar la incertidumbre.',
      'Usa entre 2 y 200 réplicas (20 por defecto).',
    );
  }
  if (!Number.isInteger(ca.dias) || ca.dias < 1 || ca.dias > 30) {
    bloquea(
      'parametro-dias',
      'Horizonte inválido',
      'Simulación',
      'El horizonte es la cantidad de días simulados.',
      'Usa entre 1 y 30 días.',
    );
  }
  if (!Number.isInteger(ca.semilla) || ca.semilla < 0) {
    bloquea(
      'parametro-semilla',
      'Semilla inválida',
      'Simulación',
      'La semilla hace reproducible la simulación.',
      'Usa un número entero mayor o igual a cero.',
    );
  }
  return r;
}
